import { playSound, topicDisplayName, topicShortUrl } from "./utils";
import { notificationTag, toNotificationParams } from "./notificationUtils";
import prefs from "./Prefs";
import routes from "../components/routes";
import nativeBridge from "./NativeBridge";
import { command } from "./NativeBridgeContract.generated";
import type { Subscription, Notification } from "./types";

/**
 * The notifier is responsible for displaying desktop notifications. Note that not all modern browsers
 * support this; most importantly, all iOS browsers do not support window.Notification.
 */
class Notifier {
  lastSoundPlayedAt = 0;

  async notify(subscription: Subscription, notification: Notification) {
    if (nativeBridge.isNativeApp()) {
      return;
    }
    if (!this.supported()) {
      return;
    }

    await this.playSound();

    const shortUrl = topicShortUrl(subscription.baseUrl, subscription.topic);
    const defaultTitle = topicDisplayName(subscription);

    console.log(`[Notifier, ${shortUrl}] Displaying notification ${notification.id}`);

    const params = toNotificationParams({
      message: notification,
      defaultTitle,
      // 누르면 그 토픽 화면에서 이 알림의 상세 팝업이 열린다(앱 알림과 같은 딥링크, Notifications.useDeepLinkedNotification)
      topicRoute: new URL(`${routes.forSubscription(subscription)}?message=${encodeURIComponent(notification.id)}`, window.location.origin).toString(),
      baseUrl: subscription.baseUrl,
      topic: subscription.topic,
    });
    const registration = await navigator.serviceWorker?.getRegistration();
    if (registration) {
      await registration.showNotification(...params);
    } else {
      // 서비스 워커가 없을 때(dev 등) — 클릭하면 그 알림의 상세 팝업으로
      const n = new Notification(...params);
      n.onclick = () => {
        window.focus();
        window.location.href = params[1].data.topicRoute;
      };
    }
  }

  async cancel(subscription: Subscription, notification: Notification) {
    if (nativeBridge.isNativeApp()) {
      return;
    }
    if (!this.supported()) {
      return;
    }
    try {
      const sequenceId = notification.sequence_id || notification.id;
      const tag = notificationTag(subscription.baseUrl, subscription.topic, sequenceId);
      console.log(`[Notifier] Cancelling notification with tag ${tag}`);
      const registration = await this.serviceWorkerRegistration();
      const notifications = await registration.getNotifications({ tag });
      notifications.forEach((n) => n.close());
    } catch (e) {
      console.log(`[Notifier] Error cancelling notification`, e);
    }
  }

  async playSound() {
    // Play sound, but not more than once every 2 seconds
    const now = Date.now();
    if (now - this.lastSoundPlayedAt < 2000) {
      console.log(`[Notifier] Not playing notification sound, since it was last played <2s ago`, this.lastSoundPlayedAt);
      return;
    }
    const sound = await prefs.sound();
    if (sound && sound !== "none") {
      try {
        await playSound(sound);
        this.lastSoundPlayedAt = Date.now();
      } catch (e) {
        console.log(`[Notifier] Error playing audio`, e);
      }
    }
  }

  async serviceWorkerRegistration() {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      throw new Error("No service worker registration found");
    }
    return registration;
  }

  notRequested() {
    if (nativeBridge.isNativeApp()) {
      return false;
    }
    return this.supported() && Notification.permission === "default";
  }

  granted() {
    if (nativeBridge.isNativeApp()) {
      return true;
    }
    return this.supported() && Notification.permission === "granted";
  }

  denied() {
    if (nativeBridge.isNativeApp()) {
      return false;
    }
    return this.supported() && Notification.permission === "denied";
  }

  async maybeRequestPermission() {
    if (nativeBridge.post(command.requestNotificationPermission)) {
      return true;
    }
    if (!this.supported()) {
      return false;
    }

    return new Promise<boolean>((resolve) => {
      Notification.requestPermission((permission) => {
        resolve(permission === "granted");
      });
    });
  }

  supported() {
    return this.browserSupported() && this.contextSupported();
  }

  browserSupported() {
    if (nativeBridge.isNativeApp()) {
      return true;
    }
    return "Notification" in window;
  }

  /**
   * Returns true if this is a HTTPS site, or served over localhost. Otherwise the Notification API
   * is not supported, see https://developer.mozilla.org/en-US/docs/Web/API/notification
   */
  contextSupported() {
    return window.location.protocol === "https:" || window.location.hostname.match("^127.") || window.location.hostname === "localhost";
  }

  /** 예전 웹 푸시용(iOS 는 설치해야 푸시 가능) — 웹 푸시를 뺐으므로 항상 false */
  iosSupportedButInstallRequired() {
    return false;
  }
}

const notifier = new Notifier();
export default notifier;
